import { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useCurrentLanguage } from '@/hooks/useCurrentLanguage';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { useQuizCreatorCreateQuizMutation } from '@/modules/corporate/modules/quiz/views/QuizCreator/graphql/mutations/generated/createQuiz';
import { getCreateQuizInput } from '@/modules/corporate/modules/quiz/views/QuizCreator/hooks/useCreateQuiz/utils';

interface UseCreateQuizInBlockOptions {
  onCreated: (quizId: string) => void;
}

const useCreateQuizInBlock = ({ onCreated }: UseCreateQuizInBlockOptions) => {
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbar();
  const user = useUserContext();
  const [{ fetching }, executeCreateQuiz] = useQuizCreatorCreateQuizMutation();
  const currentLanguage = useCurrentLanguage();
  const { site } = useAppConfig();
  const { [FeatureFlags.EnabledQuizSyncSkillScore]: isQuizSyncSkillScoreEnabled } =
    useFeatureFlags();
  const { enableAssignmentRestriction, supportedLanguages } = site;

  const onCreate = useCallback(
    (name: string) => {
      const defaultLanguageType = supportedLanguages?.includes(currentLanguage)
        ? currentLanguage
        : undefined;

      executeCreateQuiz({
        input: {
          ...getCreateQuizInput({
            name,
            t,
            currentSubWorkspace: user.currentSubWorkspace,
            enableAssignmentRestriction,
            isQuizSyncSkillScoreEnabled,
            defaultLanguageType,
          }),
          maintainDraftState: true,
        },
      }).then((response) => {
        const quizId = response.data?.createQuiz.quiz?.id;
        if (quizId) {
          onCreated(quizId);
        } else {
          showErrorSnackbar({ message: t('QuizCreator:QuizCreateError') });
        }
      });
    },
    [
      currentLanguage,
      enableAssignmentRestriction,
      executeCreateQuiz,
      isQuizSyncSkillScoreEnabled,
      onCreated,
      showErrorSnackbar,
      supportedLanguages,
      t,
      user.currentSubWorkspace,
    ],
  );

  return { creating: fetching, onCreate };
};

export default useCreateQuizInBlock;
